import dayjs, { Dayjs } from 'dayjs';
import { TFunction, useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { PrintHeader } from 'business/layout/print-header';
import { useProdAndPerfFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider';

interface ProdAndPerfPrintHeaderProps {
  printTitle: string;
  printDayMode?: boolean;
}

const getDateRange = (
  t: TFunction,
  startDate: Dayjs | null | undefined,
  endDate: Dayjs | null | undefined,
) => {
  const locale = getLocale();

  if (startDate && endDate) {
    return t('common.printTitle.dateRange', {
      startDate: dayjs.tz(startDate).locale(locale).format('L'),
      endDate: dayjs.tz(endDate).locale(locale).format('L'),
    });
  }

  if (startDate) {
    return t('common.printTitle.startDate', {
      startDate: dayjs.tz(startDate).locale(locale).format('L'),
    });
  }

  if (endDate) {
    return t('common.printTitle.endDate', {
      endDate: dayjs.tz(endDate).locale(locale).format('L'),
    });
  }

  return null;
};

const getRingRange = (
  t: TFunction,
  startRing: number | null | undefined,
  endRing: number | null | undefined,
) => {
  if (startRing && endRing) {
    return t('common.printTitle.ringRange', {
      startRing,
      endRing,
    });
  }

  if (startRing) {
    return t('common.printTitle.startRing', {
      startRing,
    });
  }

  if (endRing) {
    return t('common.printTitle.endRing', {
      endRing,
    });
  }

  return null;
};

export const ProdAndPerfPrintHeader = ({
  printTitle,
  printDayMode,
}: ProdAndPerfPrintHeaderProps) => {
  const { t } = useTranslation();
  const filters = useProdAndPerfFilters();

  const { filterType, dayMode } = filters;
  let dateRange, ringRange;

  if (filterType === 'date') {
    dateRange = filters.dateRange;
  }
  if (filterType === 'ring') {
    ringRange = filters.ringRange;
  }

  const range = {
    date: getDateRange(t, dateRange?.[0], dateRange?.[1]),
    ring: getRingRange(t, ringRange?.[0], ringRange?.[1]),
  };

  const mode = {
    shift: t('productionAndPerformance.print.shift'),
    calendar: t('productionAndPerformance.print.calendar'),
  };

  return (
    <PrintHeader>
      <h1>{printTitle}</h1>
      <div>
        {range[filterType] ? <h3>{range[filterType]}</h3> : null}
        {printDayMode ? (
          <p>
            {t('productionAndPerformance.print.startOfDay')}
            {mode[dayMode]}
          </p>
        ) : null}
      </div>
    </PrintHeader>
  );
};
